import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'assets/icons';
import Button from 'components/button';
import PanelCard, { cardIconButtonClass } from 'components/card/PanelCard';
import useOpenInvoiceDetail from 'components/invoiceDetail/useOpenInvoiceDetail';
import {
  fillText,
  monthLabel,
} from 'components/invoiceImport/invoiceImportText';
import { formatEuros } from 'components/invoiceList/invoiceAmount';
import InvoiceStatusBadge from 'components/invoiceList/InvoiceStatusBadge';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

/** "Avril 2026" for "2026-04". */
export const periodLabel = (period) => {
  const label = monthLabel(period);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const HEADER_CELL_CLASS =
  'px-2 py-2 font-normal text-gray-500 text-left uppercase tracking-wide';

/**
 * The company's invoices, one year at a time (JBWEB-1308). A row opens the
 * invoice detail (JBWEB-1274), which takes the place of the company panel.
 * Under it, the account statement in PDF over a chosen period, in a modal.
 */
const CompanyBillingHistoryCard = ({
  idCompany,
  companyName,
  refreshKey = 0,
}) => {
  const { language, setModalData } = UseContext();
  const { toggle } = useModal();
  const openInvoiceDetail = useOpenInvoiceDetail();
  const currentYear = new Date().getFullYear();
  const [requestedYear, setRequestedYear] = useState(currentYear);
  const [history, setHistory] = useState(null);
  // Kept across reads: the previous year stays reachable while one loads.
  const [knownYears, setKnownYears] = useState([]);
  const [status, setStatus] = useState('loading');
  const [retryCount, setRetryCount] = useState(0);
  // The company and year on screen: read again (`refreshKey`), they stay there
  // while the new answer comes, and a failure leaves them.
  const loadedRead = useRef(null);
  const text = (key, fallback) =>
    language.getValue(`companyBillingHistory.${key}`, fallback);

  useEffect(() => {
    if (!idCompany) return undefined;
    // An answer for a year the user has since left must not replace the one
    // asked for after it.
    let isCurrent = true;
    const read = `${idCompany}:${requestedYear}`;
    const isRefresh = loadedRead.current === read;
    if (!isRefresh) setStatus('loading');
    ApiDatabase.getCompanyInvoices(
      { idCompany, year: requestedYear },
      (response) => {
        if (!isCurrent) return;
        loadedRead.current = read;
        const data = response?.data ?? {};
        setHistory({
          year: data.year ?? requestedYear,
          items: data.items ?? [],
        });
        setKnownYears(data.years ?? []);
        setStatus('ready');
      },
      (error) => {
        if (!isCurrent || error?.name === 'CanceledError' || isRefresh) return;
        setStatus('error');
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [idCompany, requestedYear, retryCount, refreshKey]);

  const shownYear =
    status === 'ready' && history ? history.year : requestedYear;
  const oldestYear = knownYears.length ? Math.min(...knownYears) : null;
  const isPreviousDisabled = oldestYear === null || shownYear <= oldestYear;
  const isNextDisabled = shownYear >= currentYear;

  // The arrows sit on the header that folds the card: neither a click nor
  // Enter/Space on them may reach it.
  const stopFolding = (event) => event.stopPropagation();

  const yearSelector = (
    <div className={'flex items-center gap-2'}>
      <button
        type={'button'}
        aria-label={text('previousYear', 'Année précédente')}
        className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
        disabled={isPreviousDisabled}
        onClick={(event) => {
          event.stopPropagation();
          setRequestedYear(shownYear - 1);
        }}
        onKeyDown={stopFolding}
      >
        <ChevronLeft wh={18} color={'#374151'} />
      </button>
      <span
        className={
          'border border-gray-300 rounded-md h-8 px-4 flex items-center font-medium text-gray-900'
        }
      >
        {shownYear}
      </span>
      <button
        type={'button'}
        aria-label={text('nextYear', 'Année suivante')}
        className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
        disabled={isNextDisabled}
        onClick={(event) => {
          event.stopPropagation();
          setRequestedYear(shownYear + 1);
        }}
        onKeyDown={stopFolding}
      >
        <ChevronRight wh={18} color={'#374151'} />
      </button>
    </div>
  );

  const openAccountStatement = () => {
    setModalData({ idCompany, companyName });
    toggle('company_account_statement');
  };

  const accountStatementButton = (
    <div className={'flex justify-end px-1 pb-1'}>
      <Button
        size={'SM'}
        color={'SECONDARY'}
        disabled={!idCompany}
        onClick={openAccountStatement}
      >
        {language.getValue('accountStatement.button', 'Relevé de compte')}
      </Button>
    </div>
  );

  const content = () => {
    if (status === 'loading') {
      return (
        <div className={'px-4 py-3 text-gray-500'}>
          {text('loading', 'Chargement…')}
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className={'px-4 py-3 flex items-center justify-between gap-2'}>
          <span className={'text-red-500'}>
            {text(
              'loadError',
              'L’historique de facturation n’a pas pu être chargé.',
            )}
          </span>
          <button
            type={'button'}
            className={
              'border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 flex-shrink-0'
            }
            onClick={() => setRetryCount((count) => count + 1)}
          >
            {text('retry', 'Réessayer')}
          </button>
        </div>
      );
    }
    if (!history.items.length) {
      return (
        <div className={'px-4 py-3 text-gray-500'}>
          {fillText(text('empty', 'Aucune facture en {year}'), {
            year: history.year,
          })}
        </div>
      );
    }
    return (
      <table className={'w-full text-sm'}>
        <thead>
          <tr className={'border-b border-gray-100'}>
            <th className={`${HEADER_CELL_CLASS} pl-4`}>
              {text('columnPeriod', 'Période')}
            </th>
            <th className={HEADER_CELL_CLASS}>{text('columnNumber', 'N°')}</th>
            <th className={HEADER_CELL_CLASS}>
              {text('columnAmount', 'Montant TTC')}
            </th>
            <th className={`${HEADER_CELL_CLASS} pr-4`}>
              {text('columnPayment', 'Paiement')}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.items.map((item) => (
            <tr
              key={item.number}
              className={
                'border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer'
              }
              onClick={() => openInvoiceDetail(item.number)}
            >
              <td className={'py-3 pl-4 pr-2 whitespace-nowrap'}>
                {periodLabel(item.period)}
              </td>
              <td className={'px-2 py-3 whitespace-nowrap'}>
                {/* The keyboard way in: its click bubbles to the row, which
                    opens the detail, so it carries no handler of its own. */}
                <button
                  type={'button'}
                  className={
                    'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400'
                  }
                  aria-label={fillText(
                    text('openInvoice', 'Voir la facture {number}'),
                    { number: item.number },
                  )}
                >
                  {item.number}
                </button>
              </td>
              <td className={'px-2 py-3 whitespace-nowrap'}>
                {formatEuros(item.amountInclTax)}
              </td>
              <td className={'py-3 pl-2 pr-4'}>
                <InvoiceStatusBadge invoice={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <PanelCard
      title={text('cardTitle', 'Historique de facturation')}
      badge={yearSelector}
      collapsible
      footer={accountStatementButton}
    >
      {content()}
    </PanelCard>
  );
};

export default CompanyBillingHistoryCard;
